import type { RandomSymbolProvider } from './RandomSymbolProvider.js';
import { columnTargetToStrip, type ColumnTarget } from './ColumnTarget.js';

/** Context passed through the middleware pipeline. */
export interface FrameContext {
  /** Reel column index. */
  readonly reelIndex: number;
  /** Total visible cells. */
  readonly visibleCells: number;
  /** Buffer symbols above visible area. */
  readonly bufferStart: number;
  /** Buffer symbols below visible area. */
  readonly bufferEnd: number;
  /** The symbol array being built (buffer + visible + buffer). Mutable by middleware. */
  symbols: string[];
  /**
   * This reel's target column from `setResult()` / `initialFrame()`, if
   * available. Read it with `getTargetSlot(target, cell)` (cell `0` is the
   * first visible cell; negative cells are buffer-above).
   */
  readonly target?: ColumnTarget;
  /** Whether the reel is currently spinning. */
  readonly isSpinning: boolean;
  /** Arbitrary metadata middleware can use to communicate. */
  metadata: Record<string, unknown>;
}

/** Middleware that participates in frame building. */
export interface FrameMiddleware {
  readonly name: string;
  /** Lower priority runs first. */
  readonly priority: number;
  process(context: FrameContext, next: () => void): void;
}

/**
 * Builds symbol frames using a middleware pipeline.
 *
 * Built-in middleware handles random fill and target placement.
 * Users can inject custom middleware for features like multiplier encoding
 * or triple-prevention.
 */
export class FrameBuilder {
  private _middlewares: FrameMiddleware[] = [];
  private _sorted = false;

  constructor(private _randomProvider: RandomSymbolProvider) {
    this.use(new RandomFillMiddleware(_randomProvider));
    this.use(new TargetPlacementMiddleware());
  }

  /** Add a middleware to the pipeline. */
  use(middleware: FrameMiddleware): this {
    this._middlewares.push(middleware);
    this._sorted = false;
    return this;
  }

  /** Remove a middleware by name. */
  remove(name: string): this {
    this._middlewares = this._middlewares.filter((m) => m.name !== name);
    return this;
  }

  /** Build a frame for a single reel. */
  build(
    reelIndex: number,
    visibleCells: number,
    bufferStart: number,
    bufferEnd: number,
    target?: ColumnTarget,
    isSpinning: boolean = false,
  ): string[] {
    if (!this._sorted) {
      this._middlewares.sort((a, b) => a.priority - b.priority);
      this._sorted = true;
    }

    const totalSlots = bufferStart + visibleCells + bufferEnd;
    const context: FrameContext = {
      reelIndex,
      visibleCells,
      bufferStart,
      bufferEnd,
      symbols: new Array<string>(totalSlots).fill(''),
      target,
      isSpinning,
      metadata: {},
    };

    // Run middleware chain
    let index = 0;
    const next = (): void => {
      if (index < this._middlewares.length) {
        const mw = this._middlewares[index++];
        mw.process(context, next);
      }
    };
    next();

    return context.symbols;
  }

  /** Build frames for all reels. */
  buildAll(
    reelCount: number,
    visibleCells: number,
    bufferStart: number,
    bufferEnd: number,
    targets?: ColumnTarget[],
    isSpinning: boolean = false,
  ): string[][] {
    return Array.from({ length: reelCount }, (_, reelIndex) =>
      this.build(
        reelIndex,
        visibleCells,
        bufferStart,
        bufferEnd,
        targets?.[reelIndex],
        isSpinning,
      ),
    );
  }

  /**
   * @internal `RandomSymbolProvider` was hidden from the package entry in
   * 1.0.0 (PR #140); this getter re-exposed the type. Middleware that needs a
   * random symbol should read it from the `FrameContext.symbols` slot it is
   * filling, or carry its own provider - weights are configured through
   * `builder.weights({...})`.
   */
  get randomProvider(): RandomSymbolProvider {
    return this._randomProvider;
  }

  get middleware(): ReadonlyArray<FrameMiddleware> {
    return this._middlewares;
  }
}

/** Fills empty symbol slots with random symbols. OCCUPIED cells are kept verbatim. */
class RandomFillMiddleware implements FrameMiddleware {
  readonly name = 'random-fill';
  readonly priority = 0;

  constructor(private _provider: RandomSymbolProvider) {}

  process(context: FrameContext, next: () => void): void {
    for (let i = 0; i < context.symbols.length; i++) {
      if (!context.symbols[i]) {
        // Each slot names the pool it belongs to: the visible window, or
        // whichever side of the buffer it sits on.
        const slot =
          i < context.bufferStart
            ? 'bufferStart'
            : i >= context.bufferStart + context.visibleCells
              ? 'bufferEnd'
              : 'spinning';
        context.symbols[i] = this._provider.next(slot, context.reelIndex);
      }
    }
    next();
  }
}

/** Places the target column (from setResult) onto the strip. */
class TargetPlacementMiddleware implements FrameMiddleware {
  readonly name = 'target-placement';
  readonly priority = 10;

  process(context: FrameContext, next: () => void): void {
    if (context.target) {
      const strip = columnTargetToStrip(context.target, context.bufferStart);
      const count = Math.min(strip.length, context.symbols.length);
      for (let i = 0; i < count; i++) {
        const id = strip[i];
        if (id) context.symbols[i] = id;
      }
    }
    next();
  }
}
